import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Clock,
  Download,
  Plus,
  Send,
  ShieldCheck,
  X,
} from 'lucide-react';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import apiService from '../../services/api';
import {
  NR2J_CATALOG_BILLING_TYPES,
  NR2J_VAT_OPTIONS,
  centsToEuros,
  computeQuoteLineTotals,
  effectiveContractStatus,
  eurosToCents,
  formatCentsAsEuros,
  formatNr2jDate,
  nr2jContractListBadge,
  organizationDisplayName,
} from '../../utils/nr2jConstants';
import Nr2jContractLivePreviewPanel from '../../components/nr2j/Nr2jContractLivePreviewPanel.jsx';
import Nr2jContractSignatoryField from '../../components/nr2j/Nr2jContractSignatoryField.jsx';
import {
  approvalErrorMessage,
  approvalUserName,
  isApprovalRequiredError,
} from '../../utils/nr2jApprovalUi.js';
import { formatNr2jContractGateError, extractNr2jContractGateMissing } from '../../utils/nr2jContractGateUi.js';
import { resolveNr2jContractDraftActions } from '../../utils/nr2jContractDraftActions.js';
import Nr2jContinueModal from '../../components/nr2j/Nr2jContinueModal.jsx';
import { useNr2jContinueAction } from '../../hooks/useNr2jContinueAction.js';
import {
  NR2J_CONTINUE_INTENTS,
  preflightContractSend,
  requirementsFromError,
  buildContinueRequirements,
} from '../../utils/nr2jContinuePreflight.js';

function emptyLine() {
  return {
    key: `${Date.now()}-${Math.random()}`,
    catalogItemId: '',
    name: '',
    description: '',
    quantity: 1,
    unitPriceHtCents: 0,
    priceEuros: '',
    vatRateBps: 2000,
    billingType: 'ONE_TIME',
    discountMode: 'none',
    discountPercent: '',
    discountEuros: '',
  };
}

function lineFromApi(line) {
  let discountMode = 'none';
  let discountPercent = '';
  let discountEuros = '';
  if (line.discountPercentBps != null && line.discountPercentBps !== '') {
    discountMode = 'percent';
    discountPercent = String(Number(line.discountPercentBps) / 100);
  } else if (line.discountAmountHtCents != null && line.discountAmountHtCents !== '') {
    discountMode = 'fixed';
    discountEuros = centsToEuros(line.discountAmountHtCents);
  }
  return {
    key: line.id || `${Date.now()}-${Math.random()}`,
    catalogItemId: line.catalogItemId || '',
    name: line.name || '',
    description: line.description || '',
    quantity: line.quantity || 1,
    unitPriceHtCents: line.unitPriceHtCents || 0,
    priceEuros: centsToEuros(line.unitPriceHtCents),
    vatRateBps: line.vatRateBps ?? 2000,
    billingType: line.billingType || 'ONE_TIME',
    discountMode,
    discountPercent,
    discountEuros,
  };
}

function lineToPayload(line, index) {
  const unitPriceHtCents = line.priceEuros !== ''
    ? eurosToCents(line.priceEuros)
    : Number(line.unitPriceHtCents) || 0;
  const payload = {
    catalogItemId: line.catalogItemId || null,
    sortOrder: index,
    name: line.name.trim(),
    description: line.description?.trim() || null,
    quantity: Math.max(1, Number(line.quantity) || 1),
    unitPriceHtCents,
    vatRateBps: Number(line.vatRateBps),
    billingType: line.billingType,
    discountPercentBps: null,
    discountAmountHtCents: null,
  };
  if (line.discountMode === 'percent' && line.discountPercent !== '') {
    payload.discountPercentBps = Math.round(Number(line.discountPercent) * 100);
  } else if (line.discountMode === 'fixed' && line.discountEuros !== '') {
    payload.discountAmountHtCents = eurosToCents(line.discountEuros);
  }
  return payload;
}

function publishedTemplateOptions(templates) {
  const options = [];
  for (const t of templates || []) {
    for (const v of t.versions || []) {
      if (v.publishedAt) {
        options.push({
          id: v.id,
          label: `${t.name} — v${v.version}`,
        });
      }
    }
  }
  return options;
}

export default function Nr2jContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toasts, toast, removeToast } = useToast();
  const {
    continueReq,
    openContinue,
    closeContinue,
    runContinueRetry,
    withActionLock,
  } = useNr2jContinueAction();

  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [org, setOrg] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [lines, setLines] = useState([]);
  const [signatoryContactId, setSignatoryContactId] = useState('');
  const [templateVersionId, setTemplateVersionId] = useState('');
  const [clientNote, setClientNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [cancelNote, setCancelNote] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);

  const version = contract?.currentVersion;
  const effStatus = version ? (version.effectiveStatus || effectiveContractStatus(version)) : null;
  const approvalInfo = contract?.approval || null;
  const latestApproval = approvalInfo?.latest || null;
  const approvalStatus = latestApproval?.status || contract?.approvalStatus || null;
  const isDraft = version?.status === 'DRAFT';
  const isSent = effStatus === 'SENT';
  const draftActions = resolveNr2jContractDraftActions({
    isDraft,
    approvalInfo,
    approvalStatus,
  });
  const {
    needsApproval,
    approvalPending,
    approvalGranted,
    showSendDraft,
    showSubmitDraft,
  } = draftActions;
  const canEdit = isDraft && !(needsApproval && approvalPending);
  const showApprovalBanner = Boolean(latestApproval) && isDraft;
  const badge = nr2jContractListBadge(effStatus, approvalStatus);
  const templateOptions = useMemo(() => publishedTemplateOptions(templates), [templates]);

  function mergeOrgContacts(updater) {
    setOrg((prev) => updater(prev));
  }

  function applyVersionToForm(v) {
    setLines((v?.lines || []).length ? v.lines.map(lineFromApi) : []);
    setSignatoryContactId(v?.signatoryContactId || '');
    setTemplateVersionId(v?.templateVersionId || '');
    setClientNote(v?.clientNote || '');
    setInternalNote(v?.internalNote || '');
  }

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [contractRes, catalogRes, templatesRes] = await Promise.all([
        apiService.getNr2jContract(id),
        apiService.listNr2jCatalog({ active: true }).catch(() => ({ items: [] })),
        apiService.listNr2jContractTemplates().catch(() => ({ templates: [] })),
      ]);
      const c = contractRes?.contract || contractRes;
      setContract(c);
      setCatalog(catalogRes?.items || []);
      const list = templatesRes?.templates || templatesRes || [];
      setTemplates(Array.isArray(list) ? list : []);
      applyVersionToForm(c?.currentVersion);
      if (c?.organizationId) {
        try {
          const detail = await apiService.getNr2jOrganization(c.organizationId);
          setOrg(detail?.organization || detail);
        } catch {
          setOrg(c.organization || null);
        }
      }
    } catch (error) {
      if (!silent) {
        toast.error(error?.message || 'Contrat introuvable');
        navigate('/nr2j/contrats');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const st = version ? (version.effectiveStatus || effectiveContractStatus(version)) : null;
    if (st !== 'SENT') return undefined;
    const timer = setInterval(() => {
      load({ silent: true });
    }, 5000);
    return () => clearInterval(timer);
  }, [version?.status, version?.signedAt, version?.effectiveStatus, load]);

  async function saveDraft() {
    if (!canEdit) return;
    if (!signatoryContactId) {
      toast.error('Sélectionnez un signataire');
      return;
    }
    setSaving(true);
    try {
      const res = await apiService.patchNr2jContract(id, {
        templateVersionId: templateVersionId || null,
        signatoryContactId,
        notes: {
          clientNote: clientNote || null,
          internalNote: internalNote || null,
        },
        lines: lines.map(lineToPayload),
      });
      const c = res?.contract || res;
      setContract(c);
      applyVersionToForm(c?.currentVersion);
      toast.success('Brouillon enregistré');
    } catch (error) {
      toast.error(error?.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  }

  function openContractGateContinue(error, intent, retryFn, ctx = {}) {
    const orgCtx = ctx.org ?? org;
    const contactsCtx = ctx.contacts ?? orgCtx?.contacts ?? org?.contacts ?? [];
    const signatoryIdCtx = ctx.signatoryContactId ?? signatoryContactId;
    const missing = extractNr2jContractGateMissing(error);

    const reqFromError = requirementsFromError(error, {
      intent,
      org: orgCtx,
      contacts: contactsCtx,
      signatoryContactId: signatoryIdCtx,
      contractId: id,
    }) || (missing.length ? buildContinueRequirements({
      intent,
      missingKeys: missing,
      org: orgCtx,
      contacts: contactsCtx,
      signatoryContactId: signatoryIdCtx,
      contractId: id,
    }) : null);

    if (reqFromError) {
      openContinue(reqFromError, retryFn);
      return true;
    }

    const pre = preflightContractSend({
      org: orgCtx,
      contacts: contactsCtx,
      signatoryContactId: signatoryIdCtx,
      contract,
      version: contract?.currentVersion,
    });
    if (pre) {
      openContinue(pre, retryFn);
      return true;
    }
    return false;
  }

  async function submitForApproval() {
    const run = async () => {
      setActionBusy(true);
      try {
        if (canEdit) await saveDraft();
        const pre = preflightContractSend({
          org,
          contacts: org?.contacts || [],
          signatoryContactId,
          contract,
          version: contract?.currentVersion,
        });
        if (pre) {
          openContinue({ ...pre, intent: NR2J_CONTINUE_INTENTS.CONTRACT_SUBMIT }, () => submitForApproval());
          return;
        }
        const res = await apiService.submitNr2jContract(id);
        const approver = res?.approval?.assignedApprover;
        const approverName = !approver || approver.role === 'SUPER_ADMIN'
          ? "l'administrateur"
          : approvalUserName(approver, "l'administrateur");
        toast.success(`Contrat soumis à ${approverName} pour validation.`);
        await load({ silent: true });
      } catch (error) {
        if (openContractGateContinue(error, NR2J_CONTINUE_INTENTS.CONTRACT_SUBMIT, () => submitForApproval())) {
          return;
        }
        toast.error(
          formatNr2jContractGateError(
            error,
            approvalErrorMessage(error, 'Soumission pour validation impossible'),
          ),
        );
        await load({ silent: true });
      } finally {
        setActionBusy(false);
      }
    };
    await withActionLock(run);
  }

  function resumeSendContractFromContinuePayload(payload, ctx = {}) {
    return sendContract({
      fromContinue: true,
      org: payload?.org,
      contacts: payload?.contacts,
      signatoryContactId: payload?.signatoryContactId ?? ctx.signatoryContactId,
      skipPreflight: true,
    });
  }

  async function sendContract(options = {}) {
    const run = async () => {
      setActionBusy(true);
      try {
        if (canEdit && !options.fromContinue) await saveDraft();
        const orgCtx = options.org ?? org;
        const contactsCtx = options.contacts ?? orgCtx?.contacts ?? org?.contacts ?? [];
        const signatoryIdCtx = options.signatoryContactId ?? signatoryContactId;

        if (options.org) {
          setOrg((prev) => ({
            ...(prev || {}),
            ...options.org,
            contacts: options.contacts || prev?.contacts,
          }));
        } else if (options.contacts?.length) {
          mergeOrgContacts((prev) => (prev ? { ...prev, contacts: options.contacts } : prev));
        }
        if (options.signatoryContactId) {
          setSignatoryContactId(options.signatoryContactId);
        }

        if (!options.skipPreflight) {
          const pre = preflightContractSend({
            org: orgCtx,
            contacts: contactsCtx,
            signatoryContactId: signatoryIdCtx,
            contract,
            version: contract?.currentVersion,
          });
          if (pre) {
            openContinue(pre, (payload) => resumeSendContractFromContinuePayload(payload, {
              signatoryContactId: signatoryIdCtx,
            }));
            return;
          }
        }
        const res = await apiService.sendNr2jContract(id);
        setContract(res?.contract || res);
        toast.success('Contrat envoyé pour signature');
        await load({ silent: true });
      } catch (error) {
        const orgCtx = options.org ?? org;
        const contactsCtx = options.contacts ?? orgCtx?.contacts ?? org?.contacts ?? [];
        const signatoryIdCtx = options.signatoryContactId ?? signatoryContactId;
        if (isApprovalRequiredError(error)) {
          toast.error(approvalErrorMessage(error, 'Envoi impossible'));
          await load({ silent: true });
        } else if (openContractGateContinue(
          error,
          NR2J_CONTINUE_INTENTS.CONTRACT_SEND,
          (payload) => resumeSendContractFromContinuePayload(payload, {
            signatoryContactId: signatoryIdCtx,
          }),
          {
            org: orgCtx,
            contacts: contactsCtx,
            signatoryContactId: signatoryIdCtx,
          },
        )) {
          /* modale */
        } else {
          toast.error(formatNr2jContractGateError(error, 'Envoi impossible'));
        }
      } finally {
        setActionBusy(false);
      }
    };
    if (options.fromContinue) {
      await run();
      return;
    }
    await withActionLock(run);
  }

  async function downloadPdf(mode = 'download') {
    setActionBusy(true);
    try {
      await apiService.downloadNr2jContractPdf(id, {
        number: contract?.number,
        version: version?.version,
        mode,
      });
    } catch (error) {
      toast.error(error?.message || 'PDF indisponible');
    } finally {
      setActionBusy(false);
    }
  }

  async function confirmCancel() {
    setActionBusy(true);
    try {
      const res = await apiService.cancelNr2jContract(id, {
        decisionNote: cancelNote || null,
      });
      setContract(res?.contract || res);
      setCancelOpen(false);
      setCancelNote('');
      toast.success('Contrat annulé');
      await load({ silent: true });
    } catch (error) {
      toast.error(error?.message || 'Annulation impossible');
    } finally {
      setActionBusy(false);
    }
  }

  function addCatalogLine(itemId) {
    const item = catalog.find((c) => c.id === itemId);
    if (!item) return;
    setLines((prev) => [
      ...prev,
      {
        ...emptyLine(),
        catalogItemId: item.id,
        name: item.name,
        description: item.description || '',
        priceEuros: centsToEuros(item.unitPriceHtCents),
        unitPriceHtCents: item.unitPriceHtCents,
        vatRateBps: item.vatRateBps,
        billingType: item.billingType,
      },
    ]);
  }

  if (loading || !contract) {
    return (
      <div className="p-8 text-sm text-gray-500" data-testid="nr2j-contract-loading">
        Chargement du contrat…
      </div>
    );
  }

  const canCancel = version?.status === 'DRAFT' || version?.status === 'SENT';

  return (
    <div className="space-y-6" data-testid="nr2j-contract-detail">
      <div
        className="sticky top-0 z-20 -mx-1 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200/80 bg-white/90 px-1 py-3 backdrop-blur"
        data-testid="nr2j-contract-sticky-actions"
      >
        <div className="min-w-0">
          <Link to="/nr2j/contrats" className="inline-flex items-center gap-1 text-sm text-indigo-700">
            <ArrowLeft className="h-4 w-4" /> Retour aux contrats
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">{contract.number}</h1>
            {version?.version != null ? (
              <span className="text-sm text-gray-500">v{version.version}</span>
            ) : null}
            <span
              className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs ${badge.className}`}
              data-testid="nr2j-contract-status-badge"
            >
              {badge.label}
            </span>
            {effStatus === 'SIGNED' ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-800">
                <ShieldCheck className="h-4 w-4" aria-hidden />
                Signature enregistrée
                {version?.signedAt ? ` · ${formatNr2jDate(version.signedAt)}` : ''}
              </span>
            ) : null}
          </div>
          <p className="text-sm text-gray-600">
            {organizationDisplayName(org || contract.organization)}
            {org?.id || contract.organizationId ? (
              <>
                {' · '}
                <Link
                  to={`/nr2j/clients/${org?.id || contract.organizationId}`}
                  className="text-indigo-700 hover:underline"
                >
                  Ouvrir la fiche société
                </Link>
              </>
            ) : null}
            {contract.sourceQuoteId ? (
              <>
                {' · '}
                <Link
                  to={`/nr2j/devis/${contract.sourceQuoteId}`}
                  className="text-indigo-700 hover:underline"
                >
                  Devis source{contract.sourceQuoteNumber ? ` ${contract.sourceQuoteNumber}` : ''}
                </Link>
              </>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isDraft ? (
            <>
              {canEdit ? (
                <button
                  type="button"
                  disabled={saving || actionBusy}
                  onClick={saveDraft}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium"
                >
                  Enregistrer
                </button>
              ) : null}
              <button
                type="button"
                disabled={actionBusy}
                onClick={() => downloadPdf('preview')}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium"
              >
                <Download className="mr-1 inline h-4 w-4" /> Aperçu PDF
              </button>
              {showSubmitDraft ? (
                <button
                  type="button"
                  disabled={actionBusy}
                  onClick={submitForApproval}
                  className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                  data-testid="nr2j-contract-submit-approval"
                >
                  <ShieldCheck className="h-4 w-4" />
                  {approvalStatus === 'CHANGES_REQUESTED' || approvalStatus === 'REJECTED'
                    ? 'Re-soumettre pour validation'
                    : 'Soumettre pour validation'}
                </button>
              ) : null}
              {showSendDraft ? (
                <button
                  type="button"
                  disabled={actionBusy}
                  onClick={sendContract}
                  className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white"
                  data-testid="nr2j-contract-send"
                >
                  <Send className="h-4 w-4" /> Envoyer le contrat
                </button>
              ) : null}
            </>
          ) : (
            <>
              <button
                type="button"
                disabled={actionBusy}
                onClick={() => downloadPdf('download')}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              >
                <Download className="h-4 w-4" /> Télécharger PDF
              </button>
              {isSent ? (
                <button
                  type="button"
                  disabled={actionBusy}
                  onClick={sendContract}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                >
                  <Send className="h-4 w-4" /> Renvoyer
                </button>
              ) : null}
            </>
          )}
          {canCancel ? (
            <button
              type="button"
              onClick={() => setCancelOpen(true)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
            >
              Annuler le contrat
            </button>
          ) : null}
        </div>
      </div>

      {showApprovalBanner && approvalPending ? (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          data-testid="nr2j-contract-approval-pending"
        >
          <Clock className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">En attente de validation</p>
            <p>
              Soumis à {approvalUserName(latestApproval.assignedApprover)} le{' '}
              {formatNr2jDate(latestApproval.requestedAt)}
            </p>
          </div>
        </div>
      ) : null}

      {showApprovalBanner && approvalGranted ? (
        <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">
              Validé par {approvalUserName(latestApproval.decidedBy || latestApproval.assignedApprover)}
              {latestApproval.decidedAt ? ` le ${formatNr2jDate(latestApproval.decidedAt)}` : ''}
            </p>
            <p>Vous pouvez maintenant envoyer le contrat au signataire.</p>
          </div>
        </div>
      ) : null}

      {showApprovalBanner && (approvalStatus === 'CHANGES_REQUESTED' || approvalStatus === 'REJECTED') ? (
        <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${
          approvalStatus === 'REJECTED'
            ? 'border-rose-200 bg-rose-50 text-rose-900'
            : 'border-amber-300 bg-amber-50 text-amber-900'
        }`}
        >
          <X className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">
              {approvalStatus === 'REJECTED' ? 'Refusé' : 'Modifications demandées'}
              {' '}par {approvalUserName(latestApproval.decidedBy || latestApproval.assignedApprover)}
            </p>
            {latestApproval.comment ? <p>Motif : {latestApproval.comment}</p> : null}
          </div>
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Signataire & modèle</h2>
            <Nr2jContractSignatoryField
              org={org}
              signatoryContactId={signatoryContactId}
              onSignatoryContactIdChange={setSignatoryContactId}
              onOrgContactsUpdated={mergeOrgContacts}
              disabled={!canEdit}
              allowCompleteContact={canEdit}
            />
            <label className="mt-3 block text-sm">
              <span className="text-gray-500">Modèle</span>
              <select
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50"
                value={templateVersionId}
                disabled={!canEdit}
                onChange={(e) => setTemplateVersionId(e.target.value)}
              >
                <option value="">— Modèle courant —</option>
                {templateOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
            </label>
          </section>

          <section className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-4 text-sm text-indigo-950">
            <p className="font-medium">Durée contractuelle</p>
            <p className="mt-1 text-indigo-900/90">
              Sans engagement — résiliation avec préavis écrit d’un mois (PDF contrat).
            </p>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Lignes d’offre</h2>
            {canEdit ? (
              <div className="mt-3 flex flex-wrap gap-2">
                <select
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) {
                      addCatalogLine(e.target.value);
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">Ajouter depuis le catalogue…</option>
                  {catalog.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.code} — {item.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setLines((prev) => [...prev, emptyLine()])}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <Plus className="h-4 w-4" /> Ligne libre
                </button>
              </div>
            ) : null}
            <div className="mt-4 space-y-3">
              {lines.length === 0 ? (
                <p className="text-sm text-gray-500">Aucune ligne.</p>
              ) : (
                lines.map((line, idx) => {
                  const lineTotals = computeQuoteLineTotals(lineToPayload(line, 0));
                  return (
                    <div key={line.key} className="rounded-lg border border-gray-100 p-3">
                      {canEdit ? (
                        <div className="grid gap-2 sm:grid-cols-2">
                          <label className="block text-sm sm:col-span-2">
                            <span className="text-gray-500">Désignation</span>
                            <input
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.name}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, name: e.target.value } : l
                              )))}
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">Qté</span>
                            <input
                              type="number"
                              min="1"
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.quantity}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, quantity: e.target.value } : l
                              )))}
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">PU HT (€)</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.priceEuros}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, priceEuros: e.target.value } : l
                              )))}
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">TVA</span>
                            <select
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.vatRateBps}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, vatRateBps: Number(e.target.value) } : l
                              )))}
                            >
                              {NR2J_VAT_OPTIONS.map((v) => (
                                <option key={v.bps} value={v.bps}>{v.label}</option>
                              ))}
                            </select>
                          </label>
                          <label className="block text-sm">
                            <span className="text-gray-500">Facturation</span>
                            <select
                              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                              value={line.billingType}
                              onChange={(e) => setLines((prev) => prev.map((l, i) => (
                                i === idx ? { ...l, billingType: e.target.value } : l
                              )))}
                            >
                              {NR2J_CATALOG_BILLING_TYPES.map((t) => (
                                <option key={t.id} value={t.id}>{t.label}</option>
                              ))}
                            </select>
                          </label>
                        </div>
                      ) : (
                        <div className="flex flex-wrap justify-between gap-2 text-sm">
                          <div>
                            <p className="font-medium text-gray-900">{line.name}</p>
                            <p className="text-xs text-gray-500">
                              Qté {line.quantity} · {formatCentsAsEuros(line.unitPriceHtCents || eurosToCents(line.priceEuros))} € HT
                            </p>
                          </div>
                          <p className="font-medium">{formatCentsAsEuros(lineTotals.lineTtcCents)} € TTC</p>
                        </div>
                      )}
                      {canEdit ? (
                        <div className="mt-2 flex justify-between text-xs text-gray-500">
                          <span>TTC : {formatCentsAsEuros(lineTotals.lineTtcCents)} €</span>
                          <button
                            type="button"
                            className="text-rose-700 hover:underline"
                            onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            Supprimer
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <label className="block text-sm">
              <span className="text-gray-500">Note client</span>
              <textarea
                rows={2}
                disabled={!canEdit}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50"
                value={clientNote}
                onChange={(e) => setClientNote(e.target.value)}
              />
            </label>
            <label className="mt-3 block text-sm">
              <span className="text-gray-500">Note interne</span>
              <textarea
                rows={2}
                disabled={!canEdit}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50"
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
              />
            </label>
          </section>
        </div>

        <aside className="space-y-4">
          <Nr2jContractLivePreviewPanel lines={lines} lineToPayload={lineToPayload} />
          {version?.expiresAt ? (
            <p className="rounded-xl border border-gray-200 bg-white px-5 py-3 text-xs text-gray-500">
              Lien valable jusqu’au {formatNr2jDate(version.expiresAt)}
            </p>
          ) : null}
          {version?.signedAt ? (
            <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-3 text-xs text-emerald-800">
              Signé le {formatNr2jDate(version.signedAt)}
            </p>
          ) : null}
        </aside>
      </div>

      {cancelOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Annuler le contrat ?</h3>
            <p className="mt-2 text-sm text-gray-600">
              Le contrat ne pourra plus être signé. Cette action est définitive.
            </p>
            <label className="mt-3 block text-sm">
              Motif (facultatif)
              <textarea
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                rows={2}
                value={cancelNote}
                onChange={(e) => setCancelNote(e.target.value)}
              />
            </label>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                disabled={actionBusy}
                onClick={() => setCancelOpen(false)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                Fermer
              </button>
              <button
                type="button"
                disabled={actionBusy}
                onClick={confirmCancel}
                className="rounded-lg bg-rose-700 px-3 py-2 text-sm font-medium text-white"
              >
                Confirmer l’annulation
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <Nr2jContinueModal
        open={Boolean(continueReq)}
        requirements={continueReq?.requirements}
        templateOptions={publishedTemplateOptions(templates)}
        onClose={closeContinue}
        onSaved={async (payload) => {
          if (payload?.org) {
            setOrg((prev) => ({
              ...(prev || {}),
              ...payload.org,
              contacts: payload.contacts || prev?.contacts,
            }));
          } else if (payload?.contacts?.length) {
            mergeOrgContacts((prev) => (prev ? { ...prev, contacts: payload.contacts } : prev));
          }
          if (payload?.signatoryContactId) {
            setSignatoryContactId(payload.signatoryContactId);
          }
          const retry = continueReq?.retry;
          closeContinue();
          if (retry) await runContinueRetry(retry, payload);
          await load({ silent: true });
        }}
      />

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </div>
  );
}
